import React, { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import { TailSpin } from "react-loader-spinner";

import type { BaseRowItem } from "../../components/data-table";
import type { UserReward } from "../../lib/api/useUserActivityQueries";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";

export type UserActivityRewardRow = BaseRowItem & {
  displayDate: string;
  source: string;
  description: string;
  points: string;
};

function buildRows(rewards: UserReward[]): UserActivityRewardRow[] {
  return rewards.map((reward, index) => ({
    id: reward._id ?? `reward-${index}`,
    displayDate: formatOrdinalActivityDate(reward.createdAt),
    source: reward.source ?? "—",
    description: reward.reason || reward.message || "—",
    points: reward.points != null ? String(reward.points) : "—",
  }));
}

function rewardColClass(id: string): string {
  switch (id) {
    case "displayDate":
      return "w-1/5 min-w-0";
    case "source":
      return "w-1/5 min-w-0";
    case "description":
      return "w-2/5 min-w-0";
    case "points":
      return "w-1/5 min-w-0";
    default:
      return "min-w-0";
  }
}

const columns: ColumnDef<UserActivityRewardRow, unknown>[] = [
  {
    accessorKey: "displayDate",
    header: () => (
      <span className="text-[#869AA9] text-[12px]">Date</span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white text-[13px]">{String(getValue())}</span>
    ),
  },
  {
    accessorKey: "source",
    header: () => (
      <span className="text-[#869AA9] text-[12px]">Source</span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white text-[13px]">{String(getValue())}</span>
    ),
  },
  {
    accessorKey: "description",
    header: () => (
      <span className="text-[#869AA9] text-[12px]">Description</span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white capitalize text-[13px]">
        {String(getValue())}
      </span>
    ),
  },
  {
    accessorKey: "points",
    header: () => (
      <span className="text-[#869AA9] text-[12px] text-right block w-full">
        Points
      </span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white text-[13px] text-right block w-full">
        {String(getValue())}
      </span>
    ),
  },
];

export type UserActivityRewardsTableProps = {
  rewards: UserReward[];
  isLoading: boolean;
  emptyImage: string;
};

export function UserActivityRewardsTable({
  rewards,
  isLoading,
  emptyImage,
}: UserActivityRewardsTableProps) {
  const data = useMemo(() => buildRows(rewards), [rewards]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  if (isLoading && rewards.length === 0) {
    return (
      <div className="flex mt-10 justify-center items-start">
        <TailSpin color="#0B75C2" height={50} width={70} />
      </div>
    );
  }

  if (!isLoading && rewards.length === 0) {
    return (
      <div className="flex flex-col justify-center h-[550px] items-center">
        <img src={emptyImage} alt="" />
        <h4 className="font-bold text-[#869AA9] pt-2">No recent reward</h4>
        <p className="text-[#869AA9] pt-2 text-[12px]">
          All user rewards will show up here
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#192025] font-manrope overflow-auto scrollbar-thumb-[#60B7F6] scrollbar-track-white scrollbar-thin cursor-pointer lgss:flex lgss:flex-col w-full h-[612px]">
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr
              key={hg.id}
              className="border-b border-[#3A4852] mt-8 pb-6 flex justify-start px-10 items-center"
            >
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className={`${rewardColClass(header.column.id)} text-left whitespace-nowrap overflow-hidden`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="py-6 w-full flex justify-start items-center px-10">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`${rewardColClass(cell.column.id)} text-left whitespace-nowrap overflow-hidden`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              <tr className="w-full">
                <td className="w-full h-[1px] bg-[#3A4852]" colSpan={4} />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
